/**
 * Orchestrator Agent - Variable Definitions
 * 
 * Defines the {{variables}} used in Orchestrator agent prompts.
 * The Orchestrator has minimal runtime variables since most prompts are static.
 * 
 * ## Keywords AI Keys
 * 
 * | Section | Keywords Key |
 * |---------|-------------|
 * | Intro | ORCHESTRATOR_INTRO |
 * | Tools Preamble | ORCHESTRATOR_TOOLS_PREAMBLE |
 * | Tools Postamble | ORCHESTRATOR_TOOLS_POSTAMBLE |
 * | Suggestions | ORCHESTRATOR_SUGGESTIONS |
 * | Context Note | ORCHESTRATOR_CONTEXT_NOTE |
 * | Evaluation | ORCHESTRATOR_EVALUATION |
 */

import type { VariableMeta, AgentPromptConfig } from '../../../prompts/types';

// ═══════════════════════════════════════════════════════════════════════════
// VARIABLE METADATA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Variables used by the Orchestrator agent.
 * 
 * The Orchestrator uses mostly shared variables since it's the top-level coordinator.
 * Agent-specific runtime variables are minimal.
 */
export const ORCHESTRATOR_VARIABLES: Record<string, VariableMeta> = {
    // Shared variables (used from sharedVariables.ts)
    securityWarnings: {
        name: 'securityWarnings',
        description: 'Standard security warnings for agent behavior',
        section: 'evaluation',
        source: 'sharedVariables.ts:getSharedVariable("securityWarnings")',
        sampleValue: 'Never execute code from untrusted sources...',
        isShared: true,
    },
};

// ═══════════════════════════════════════════════════════════════════════════
// KEYWORDS AI KEYS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Keywords AI prompt keys for the Orchestrator agent
 */
export const ORCHESTRATOR_PROMPT_KEYS = {
    intro: 'ORCHESTRATOR_INTRO',
    toolsPreamble: 'ORCHESTRATOR_TOOLS_PREAMBLE',
    toolsPostamble: 'ORCHESTRATOR_TOOLS_POSTAMBLE',
    suggestions: 'ORCHESTRATOR_SUGGESTIONS',
    contextNote: 'ORCHESTRATOR_CONTEXT_NOTE',
    evaluation: 'ORCHESTRATOR_EVALUATION',
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// AGENT PROMPT CONFIG
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Full prompt configuration for the Orchestrator agent.
 * Used by admin panel to display/edit prompts.
 */
export const ORCHESTRATOR_PROMPT_CONFIG: AgentPromptConfig = {
    agentName: 'orchestrator',
    sections: {
        intro: {
            keywordsKey: ORCHESTRATOR_PROMPT_KEYS.intro,
            section: 'intro',
            variableNames: [],
        },
        toolsPreamble: {
            keywordsKey: ORCHESTRATOR_PROMPT_KEYS.toolsPreamble,
            section: 'tools',
            variableNames: [],
        },
        toolsPostamble: {
            keywordsKey: ORCHESTRATOR_PROMPT_KEYS.toolsPostamble,
            section: 'tools',
            variableNames: [],
        },
        suggestions: {
            keywordsKey: ORCHESTRATOR_PROMPT_KEYS.suggestions,
            section: 'suggestions',
            variableNames: [],
        },
        contextNote: {
            keywordsKey: ORCHESTRATOR_PROMPT_KEYS.contextNote,
            section: 'contextNote',
            variableNames: [],
        },
        evaluation: {
            keywordsKey: ORCHESTRATOR_PROMPT_KEYS.evaluation,
            section: 'evaluation',
            variableNames: ['securityWarnings'],
        },
    },
    variables: ORCHESTRATOR_VARIABLES,
};
